import type { FC } from 'react';
import { Link } from 'react-router-dom';

import { CONTACT, LEGAL_ENTITY, STORE_LINKS } from '@/constants/site.constants';
import { ROUTES } from '@/routes/route.config';
import type { SiteLink } from '@/types/home.types';

interface SiteFooterProps {
  productLinks: SiteLink[];
  year: number;
}

const linkClass = 'font-site text-[14px] transition-colors hover:text-site-accent';

/* Support leads: it is the store "Support URL", and review opens it looking for
   a way to reach a human. Account deletion has to stay here too — Play requires
   the path to be prominently featured and easily discoverable. */
const LEGAL_LINKS: SiteLink[] = [
  { label: 'Support', href: ROUTES.support },
  { label: 'Terms', href: ROUTES.terms },
  { label: 'Rentals Terms', href: ROUTES.rentalTerms },
  { label: 'Privacy', href: ROUTES.privacy },
  { label: 'Child Safety', href: ROUTES.childSafety },
  { label: 'Delete account', href: ROUTES.deleteAccount },
];

const EXTERNAL_LINKS: SiteLink[] = [
  { label: 'Instagram', href: CONTACT.instagramUrl },
  { label: 'App Store', href: STORE_LINKS.ios },
  { label: 'Google Play', href: STORE_LINKS.android },
];

const SiteFooter: FC<SiteFooterProps> = ({ productLinks, year }) => (
  <footer className="bg-site-bg text-site-ink/70">
    <div className="mx-auto flex w-full max-w-[1240px] flex-wrap justify-between gap-4 px-[clamp(20px,5vw,72px)] py-7">
      <span className="flex items-center gap-3 font-site text-[14px]">
        <img
          src="/shoutt-lockup.svg"
          alt="Shoutt — powered by people around you"
          width={74}
          height={24}
          className="block h-6 w-auto"
        />© {year}{' '}
        {LEGAL_ENTITY.name} · Gujarat, India
      </span>

      <nav aria-label="Footer" className="flex flex-wrap gap-x-5 gap-y-2">
        <a href={`mailto:${CONTACT.email}`} className={linkClass}>
          {CONTACT.email}
        </a>

        {productLinks.map((link) => (
          <a key={link.href} href={link.href} className={linkClass}>
            {link.label}
          </a>
        ))}

        {LEGAL_LINKS.map((link) => (
          <Link key={link.href} to={link.href} className={linkClass}>
            {link.label}
          </Link>
        ))}

        {EXTERNAL_LINKS.map((link) => (
          <a
            key={link.href}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
          >
            {link.label}
          </a>
        ))}
      </nav>
    </div>
  </footer>
);

export default SiteFooter;
